'use client';

import { Button, Spinner } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { cn } from '@core/utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';

import { AuthSdk, UnauthSdk } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import { ErrorFallback } from './ErrorFallback';
import { FeedCard } from './FeedCard';
import {
  extractSeenIds,
  loadSeenFeedIds,
  mergeSeenIds,
  saveSeenFeedIds,
  type SeenFeedIds,
  toBodyFilter,
} from './feedSeenIds';
import styles from './FeedSkeleton.module.scss';
import { HomeFeedListAdBanner } from './HomeFeedListAdBanner';

const FEED_SIZE = 3;
const SEEN_FEED_IDS_KEY = 'wello-feed-seen-ids';

function createFeedScopeKey({
  isLogin,
  codeRegion,
  codeSubRegion,
}: FeedCardsProps & { isLogin: boolean }): string {
  return [
    'homeFeed',
    isLogin ? 'auth' : 'guest',
    codeRegion ?? '',
    codeSubRegion ?? '',
  ].join(':');
}

function FeedSkeleton() {
  return (
    <div
      className={cn(
        styles.skeleton,
        'h-dvh xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200',
      )}
    />
  );
}

function FeedEndMessage() {
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <div ref={ref} className="flex flex-col items-center gap-12 py-48">
      <div className="size-56">
        {inView && (
          <Lottie
            loop={false}
            src="/assets/jsons/check.json"
            className="size-56"
          />
        )}
      </div>
      <div className="flex flex-col items-center gap-4">
        <p className="font-600 text-Gray-900 typo-body2">
          모든 피드를 확인했어요
        </p>
        <p className="font-400 text-Gray-700 typo-body4">
          새로운 소식이 올라오면 다시 알려드릴게요
        </p>
      </div>
      <Button
        asChild
        theme="sub-line"
        className="mt-4 bg-Gray-white"
        dimensions="H32"
      >
        <CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname}>
          나의 정책 보러 가기
        </CustomLink>
      </Button>
    </div>
  );
}

interface FeedCardsProps {
  codeRegion?: string;
  codeSubRegion?: string;
}

function FeedCards({ codeRegion, codeSubRegion }: FeedCardsProps) {
  const isLogin = useAuthStore((state) => state.isLogin === true);
  const [nextPageError, setNextPageError] = useState(false);

  const [initialSeenIds] = useState<SeenFeedIds>(() =>
    loadSeenFeedIds(SEEN_FEED_IDS_KEY),
  );

  const regionQuery = {
    ...(codeRegion && { regionCode: codeRegion }),
    ...(codeSubRegion && { subRegionCode: codeSubRegion }),
  };

  const buildBody = (page: number, seenIds: SeenFeedIds) => ({
    page,
    size: FEED_SIZE,
    ...regionQuery,
    ...toBodyFilter(seenIds),
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery({
      queryKey: [
        'feed',
        isLogin ? 'auth' : 'guest',
        codeRegion ?? '',
        codeSubRegion ?? '',
        initialSeenIds,
      ] as const,
      queryFn: async ({ pageParam, signal }) => {
        const sdk = isLogin ? AuthSdk.getFeedList : UnauthSdk.findFeeds;
        const { data } = await sdk({
          body: pageParam,
          signal,
          throwOnError: true,
        });

        return data;
      },
      initialPageParam: buildBody(1, initialSeenIds),
      getNextPageParam: ({ context }, allPages) => {
        if (!context?.hasNext) return undefined;

        const allContents = allPages.flatMap(
          (page) => page.context?.contents ?? [],
        );
        const seenIds = mergeSeenIds(
          initialSeenIds,
          extractSeenIds(allContents),
        );

        return buildBody(allPages.length + 1, seenIds);
      },
    });

  // localStorage를 단일 출처로 삼아 현재까지 본 피드 ID를 누적한다.
  useEffect(() => {
    const allContents = data.pages.flatMap(
      (page) => page.context?.contents ?? [],
    );
    const seenIds = mergeSeenIds(
      loadSeenFeedIds(SEEN_FEED_IDS_KEY),
      extractSeenIds(allContents),
    );
    saveSeenFeedIds(SEEN_FEED_IDS_KEY, seenIds);
  }, [data.pages]);

  const handleFetchNextPage = async () => {
    setNextPageError(false);

    try {
      await fetchNextPage({ cancelRefetch: false });
    } catch {
      setNextPageError(true);
    }
  };

  const { ref } = useInView({
    rootMargin: '200px 0px',
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage && !nextPageError) {
        handleFetchNextPage();
      }
    },
  });

  const pages = data.pages.map((page) => page.context?.contents ?? []);
  const hasContents = pages.some((page) => page.length > 0);

  if (!hasContents)
    return (
      <div className="flex items-center justify-center py-64">
        <p className="font-400 text-Gray-600 typo-body4">아직 피드가 없어요</p>
      </div>
    );

  return (
    <div className="divide-y divide-Gray-200 xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200">
      {pages.map((pageContents, pageIndex) => (
        <Fragment key={`feed-page-${pageIndex}`}>
          {pageIndex > 0 && pageContents.length > 0 && <HomeFeedListAdBanner />}
          {pageContents.map((item, itemIndex) => (
            <FeedCard
              key={`${pageIndex}-${itemIndex}-${item.feedContentType ?? ''}-${item.contentIdx ?? ''}`}
              item={item}
              priority={pageIndex === 0 && itemIndex === 0}
            />
          ))}
        </Fragment>
      ))}

      {!hasNextPage && hasContents && <FeedEndMessage />}

      {hasNextPage && !nextPageError && (
        <div ref={ref} className="flex items-center justify-center p-16">
          {isFetchingNextPage ? (
            <Spinner />
          ) : (
            <Button
              theme="sub-line"
              dimensions="H32"
              onClick={handleFetchNextPage}
            >
              더 불러오기
            </Button>
          )}
        </div>
      )}

      {nextPageError && (
        <div className="flex flex-col items-center gap-12 p-16">
          <p className="font-400 text-Gray-600 typo-body4">
            불러오는 중 문제가 발생했습니다.
          </p>
          <Button
            theme="sub-line"
            dimensions="H32"
            onClick={handleFetchNextPage}
          >
            다시 시도
          </Button>
        </div>
      )}
    </div>
  );
}

interface FeedListProps {
  codeRegion?: string;
  codeSubRegion?: string;
}

export const FeedList = ({ codeRegion, codeSubRegion }: FeedListProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  if (isLogin === undefined) return <FeedSkeleton />;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<FeedSkeleton />}>
        <FeedCards
          key={createFeedScopeKey({
            isLogin: isLogin === true,
            codeRegion,
            codeSubRegion,
          })}
          codeRegion={codeRegion}
          codeSubRegion={codeSubRegion}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
